import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Callout, IconButton } from 'copilot-design-system';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ClientsClient from 'src/clients/ClientsClient';
import { CopyToClipboard } from 'src/components/CopyToClipboard';
import { Client, Company, User } from 'src/constants';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { useAppDispatch } from 'src/hooks/useStore';
import Button from 'src/legacy/components/Button';
import { ModalWrapper } from 'src/legacy/components/Modals';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { inviteClient } from 'src/store/clients/actions';
import { BlackHeadings, GraySmall } from 'src/theme/colors';
import { UrlUtils } from 'src/utils';
import { generatePassword } from 'src/utils/UserUtils';

export type CloseInviteModalReason = 'close' | 'success-invite';
interface InviteClientModalProps {
  open: boolean;
  onClose: (reason: CloseInviteModalReason) => void;
  clients: Client[];
  inviteLink?: string;
  onCloseSnackbar?: (snackbarKey: string, clientId: string) => JSX.Element;
  onContinue?: (inputCompany?: Company) => void;
  onInviteClick?: (cus: User[]) => void;
  onSuccessInvite?: () => void;
  showOnboardingCallout?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    instructionText: {
      color: GraySmall,
      marginTop: theme.spacing(2),
    },
    sendEmailInviteBtn: {
      height: 48,
      color: BlackHeadings,
    },
  }),
);

/**
 * Returns true if the given date is more than 1 year ago.
 * Used to determine if a client's invite password may have expired.
 */
const isOlderThanOneYear = (createdDate: string | undefined): boolean => {
  if (!createdDate) return false;
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  return new Date(createdDate) < oneYearAgo;
};

export const InviteClientModal: React.FC<InviteClientModalProps> = ({
  open,
  onClose,
  onCloseSnackbar,
  clients,
  onContinue,
  onSuccessInvite,
  showOnboardingCallout,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [sendingEmailInvite, setSendingEmailInvite] = React.useState(false);
  const showCallout = showOnboardingCallout;
  const { basePortalDomain } = useCustomDomains();
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = (reason: CloseInviteModalReason) => {
    onClose(reason);

    const singleClient = clients.at(0);

    if (onCloseSnackbar && singleClient) {
      enqueueSnackbar(
        t(($) => $.page_clients.modals.invite_clients.success, {
          count: clients.length,
        }),
        {
          content: (snackbarKey: string) =>
            onCloseSnackbar(
              snackbarKey,
              // If more than one client was created, don't send a client ID.
              clients.length === 1 ? singleClient.id : '',
            ),
        },
      );
    }
  };
  const handleSuccess = React.useCallback(() => {
    onContinue?.();
  }, [onContinue]);

  const handleSendEmailInvite = async () => {
    if (clients) {
      try {
        setSendingEmailInvite(true);
        await Promise.all(
          clients.map((client) =>
            dispatch(inviteClient(client.id, client.fields.givenName)),
          ),
        );
        onSuccessInvite?.();
      } catch (e) {
        console.error(e);
      } finally {
        setSendingEmailInvite(false);
      }
    }

    // close after sending email invitation
    handleClose('success-invite');
  };
  const singleClientInviteLink =
    clients.length === 1
      ? UrlUtils.GetFullUrl(`${basePortalDomain}/u/${clients.at(0)?.ref ?? ''}`)
      : '';
  const isSingleClientInvited =
    clients.length === 1 ? clients.at(0)?.fields.invitedBy : false;

  return (
    <ModalWrapper
      title={t(($) => $.page_companies.invite_clients_title)}
      open={open}
      width={500}
      onClose={() => handleClose('close')}
      cancelButtonLabel="Close"
      onSuccess={onContinue ? handleSuccess : undefined}
      successButtonLabel="Continue"
    >
      <div>
        {showCallout && clients.length === 1 ? (
          <Callout
            variant="neutral"
            description={t(
              ($) => $.client_experience.try_client_experience_description,
            )}
          />
        ) : null}

        {clients.length === 1 && (
          <>
            <BaseTypography
              className={classes.instructionText}
              fontType="13Regular"
            >
              {t(
                ($) =>
                  $.page_clients.modals.invite_clients.share_link_description,
              )}
            </BaseTypography>
            <Callout
              variant="neutral"
              description={
                <div
                  className="flex items-center gap-2 justify-between break-all"
                  data-testid="invite-link-text"
                >
                  {singleClientInviteLink}
                  <CopyToClipboard>
                    {({ copy }) => (
                      <IconButton
                        variant="minimal"
                        icon="Copy"
                        size="sm"
                        onClick={() => {
                          copy(singleClientInviteLink);
                          // Fire-and-forget: refresh password if client is over 1 year old
                          // This ensures the invite link works even if Cognito temp password expired
                          const client = clients.at(0);
                          if (
                            client &&
                            isOlderThanOneYear(client.createdDate)
                          ) {
                            ClientsClient.resetPassword({
                              userId: client.id,
                              newPW: generatePassword(9),
                            }).catch(() => {
                              // Silent fail - user still has the link copied
                            });
                          }
                        }}
                        data-testid="copy-link-btn"
                      />
                    )}
                  </CopyToClipboard>
                </div>
              }
            />

            <RowDivider>
              <BaseTypography>OR</BaseTypography>
            </RowDivider>
          </>
        )}

        <Button
          htmlId="send-email-invite-btn"
          fullWidth
          data-testid="send-email-invite-btn"
          variant="outlined"
          color="primary"
          size="large"
          className={classes.sendEmailInviteBtn}
          onClick={handleSendEmailInvite}
          isLoading={sendingEmailInvite}
          progressColor="primary"
        >
          {t(($) => $.page_companies.invite_clients_send_email_invite_btn, {
            action: isSingleClientInvited ? 'Resend' : 'Send',
            count: clients.length,
          })}
        </Button>
      </div>
    </ModalWrapper>
  );
};
